// frontend/src/pages/admin/TicketCategoryForm.js
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Button, Select, Checkbox, Row, Col, Table, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';

const { Option } = Select;

const TicketCategoryForm = ({ visible, onCancel, onSuccess, ticketCategory }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [allPriorities, setAllPriorities] = useState([]);
  const [assignedPriorities, setAssignedPriorities] = useState([]);
  const [priorityToAssign, setPriorityToAssign] = useState(null);
  const [assigningLoading, setAssigningLoading] = useState(false);
  
  const notificationApi = useNotificationApi();

  // Standardized notification functions matching ProductCategoryForm
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();
    
    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);
      
      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  // Fetch all available priorities
  useEffect(() => {
    if (visible && allPriorities.length === 0) {
      const fetchPriorities = async () => {
        const loadingKey = 'fetchPrioritiesLoading';
        const hideLoading = showLoading(loadingKey, 'Loading priorities...');
        
        try {
          const response = await api.get('/api/priorities');
          setAllPriorities(response.data.data || []);
        } catch (error) {
          console.error('Error fetching all priorities:', error.response?.data || error.message);
          showError('Failed to Load Priorities', error.response?.data?.message || 'Failed to load priorities.', 'fetchPrioritiesError');
        } finally {
          hideLoading();
        }
      };
      fetchPriorities();
    }
  }, [visible, allPriorities.length, showLoading, showError]);

  // Refetch assigned priorities
const refetchAssignedPriorities = useCallback(async () => {
  if (!ticketCategory?.id || allPriorities.length === 0) return;

  const loadingKey = 'refetchAssignedPrioritiesLoading';
  const hideLoading = showLoading(loadingKey, 'Loading assigned priorities...');
  setAssigningLoading(true);

  try {
    const response = await api.get(`/api/ticket-categories/${ticketCategory.id}`);
    const fetchedCategory = response.data.data;

    const transformedAssignedPriorities = (fetchedCategory.assignedPriorities || []).map(ap => {
      return {
        priorityId: ap.id,
        priorityName: ap.name,
        sortOrder: ap.sort_order,
        ...ap
      };
    });

    setAssignedPriorities(transformedAssignedPriorities);
    form.setFieldsValue({
      name: fetchedCategory.name,
      code: fetchedCategory.code,
      defaultPriorityId: fetchedCategory.defaultPriorityId,
      defaultResolutionTimeValue: fetchedCategory.defaultResolutionTimeValue,
      responseTimeValue: fetchedCategory.responseTimeValue,
      responseTimeUnit: fetchedCategory.responseTimeUnit,
      isActive: Boolean(fetchedCategory.isActive),
    });
  } catch (error) {
    console.error('Error refetching assigned priorities:', error.response?.data || error.message);
    showError('Failed to Refresh Data', error.response?.data?.message || 'Failed to refresh category data.', 'refetchAssignedPrioritiesError');
  } finally {
    setAssigningLoading(false);
    hideLoading();
  }
}, [ticketCategory, form, allPriorities, showLoading, showError]);

  // Primary useEffect for form initialization
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setAssignedPriorities([]);
      setPriorityToAssign(null);
      return;
    }
    
    form.resetFields();

    if (ticketCategory) {
        const formValues = {
        name: ticketCategory.name,
        code: ticketCategory.code,
        defaultPriorityId: ticketCategory.default_priority_id, // Changed
        defaultResolutionTimeValue: ticketCategory.default_resolution_time_value, // Changed
        responseTimeValue: ticketCategory.response_time_value, // Changed
        responseTimeUnit: ticketCategory.response_time_unit, // Changed
        isActive: Boolean(ticketCategory.is_active) // Changed
        };

      if (ticketCategory.id && allPriorities.length > 0) {
        refetchAssignedPriorities();
      }
    } else {
      form.resetFields();
      setAssignedPriorities([]);
      setPriorityToAssign(null);
      form.setFieldsValue({
        isActive: true,
        defaultResolutionTimeValue: 24,
        responseTimeValue: 1,
        responseTimeUnit: 'hour',
      });
    }
  }, [visible, ticketCategory, form, allPriorities.length, refetchAssignedPriorities]);

  // Handle form submission
const handleSubmit = async () => {
  let hideLoading;
  try {
    setLoading(true);
    hideLoading = showLoading('ticketCategorySubmitLoading', 
      ticketCategory ? 'Updating ticket category...' : 'Creating ticket category...');

    const values = await form.validateFields();
    const payload = {
      name: values.name,
      code: values.code,
      default_priority_id: Number(values.defaultPriorityId),
      default_resolution_time_value: Number(values.defaultResolutionTimeValue),
      response_time_value: Number(values.responseTimeValue),
      response_time_unit: values.responseTimeUnit,
      is_active: values.isActive,
      assignedPriorities: assignedPriorities.map(p => p.priorityId)
    };

    if (ticketCategory) {
      await api.put(`/api/ticket-categories/${ticketCategory.id}`, payload);
      showSuccess('Ticket Category Updated', 'Ticket category updated successfully!', 'ticketCategoryUpdateSuccess');
    } else {
      await api.post('/api/ticket-categories', payload);
      showSuccess('Ticket Category Created', 'New ticket category created successfully!', 'ticketCategoryCreateSuccess');
    }

    setTimeout(() => {
      if (hideLoading) hideLoading();
      onSuccess();
      onCancel();
    }, 500);
  } catch (error) {
    if (hideLoading) hideLoading();
    console.error('Form submission error:', error.response?.data || error.message, error);
    showError('Submission Failed', error.response?.data?.message || 'Failed to save ticket category.', 'ticketCategoryFormSubmitError');
  } finally {
    setLoading(false);
  }
};

  // Modified useEffect for form initialization to properly handle status
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setAssignedPriorities([]);
      setPriorityToAssign(null);
      return;
    }
    
    form.resetFields();

    if (ticketCategory) {
      const formValues = {
        name: ticketCategory.name,
        code: ticketCategory.code,
        defaultPriorityId: ticketCategory.default_priority_id,
        defaultResolutionTimeValue: ticketCategory.default_resolution_time_value,
        responseTimeValue: ticketCategory.response_time_value,
        responseTimeUnit: ticketCategory.response_time_unit,
        isActive: Boolean(ticketCategory.is_active)
      };

      form.setFieldsValue(formValues);

      if (ticketCategory.id && allPriorities.length > 0) {
        refetchAssignedPriorities();
      }
    } else {
      form.resetFields();
      setAssignedPriorities([]);
      setPriorityToAssign(null);
      form.setFieldsValue({
        isActive: true,
        defaultResolutionTimeValue: 24,
        responseTimeValue: 1,
        responseTimeUnit: 'hour',
      });
    }
  }, [visible, ticketCategory, form, allPriorities.length, refetchAssignedPriorities]);

  // Priority assignment functions
  const handleAssignPriority = async () => {
    if (!priorityToAssign || !ticketCategory?.id) return;

    const loadingKey = 'assignPriorityLoading';
    const hideLoading = showLoading(loadingKey, 'Assigning priority...');
    setAssigningLoading(true);

    try {
      await api.post(`/api/ticket-categories/${ticketCategory.id}/priorities`, { priority_id: priorityToAssign });
      showSuccess('Priority Assigned', 'Priority assigned successfully!', 'assignPrioritySuccess');
      setPriorityToAssign(null);
      await refetchAssignedPriorities();
    } catch (error) {
      console.error('Error assigning priority:', error.response?.data || error.message);
      const errorMsg = error.response?.status === 409 
        ? 'This priority is already assigned to this category.'
        : error.response?.data?.message || 'Failed to assign priority.';
      showError('Assignment Failed', errorMsg, 'assignPriorityError');
    } finally {
      setAssigningLoading(false);
      hideLoading();
    }
  };

  const handleRemoveAssignedPriority = async (priorityId) => {
    if (!ticketCategory?.id) return;

    const loadingKey = 'removePriorityLoading';
    const hideLoading = showLoading(loadingKey, 'Removing priority...');
    setAssigningLoading(true);

    try {
      await api.delete(`/api/ticket-categories/${ticketCategory.id}/priorities/${priorityId}`);
      showSuccess('Priority Removed', 'Priority removed successfully!', 'removePrioritySuccess');
      await refetchAssignedPriorities();
    } catch (error) {
      console.error('Error removing priority:', error.response?.data || error.message);
      showError('Removal Failed', error.response?.data?.message || 'Failed to remove priority.', 'removePriorityError');
    } finally {
      setAssigningLoading(false);
      hideLoading();
    }
  };

  const assignedPrioritiesColumns = [
    {
      title: 'Priority',
      dataIndex: 'priorityName',
      key: 'priorityName',
      sorter: (a, b) => (a.priorityName || '').localeCompare(b.priorityName || ''),
      render: (text, record) => {
        const orderText = (record.sort_order !== undefined && !isNaN(record.sort_order)) 
                         ? ` (Order: ${record.sort_order})` 
                         : '';
        return `${text}${orderText}`;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title={`Are you sure you want to remove "${record.priorityName}"?`}
          onConfirm={() => handleRemoveAssignedPriority(record.priorityId)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link" danger icon={<DeleteOutlined />} aria-label={`Remove ${record.priority_name}`} />
        </Popconfirm>
      ),
    },
  ];

  const availablePrioritiesForAssignment = allPriorities.filter(
    (priority) => !assignedPriorities.some((assigned) => assigned.priorityId === priority.id)
  );

  return (
    <Modal
      open={visible}
      title={ticketCategory ? 'Edit Ticket Category' : 'Add New Ticket Category'}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
      width={700}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        name="ticket_category_form"
        initialValues={{
          isActive: true,
          defaultResolutionTimeValue: 24,
          responseTimeValue: 1,
          responseTimeUnit: 'hour',
        }}
      >
        {/* Form fields remain exactly the same as before */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Category Name"
              rules={[
                { required: true, message: 'Please input the category name!' },
                { type: 'string', min: 2, message: 'Category name must be at least 2 characters.' }
              ]}
            >
              <Input placeholder="e.g., Severity Level 1" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="code"
              label="Category Code"
              rules={[
                { required: true, message: 'Please input the category code!' },
                { type: 'string', min: 2, max: 10, message: 'Category code must be between 2 and 10 characters.' }
              ]}
            >
              <Input placeholder="e.g., SL1" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="defaultPriorityId"
              label="Default Priority"
              rules={[{ required: true, message: 'Please select a default priority!' }]}
            >
              <Select placeholder="Select a default priority">
                {allPriorities.map(priority => (
                  <Option key={priority.id} value={priority.id}>
                    {priority.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="defaultResolutionTimeValue"
              label="Resolution Time (hours)"
              rules={[
                { required: true, message: 'Please input the default resolution time!' },
                { type: 'number', transform: Number, min: 0, message: 'Resolution time must be a non-negative number.' }
              ]}
            >
              <Input type="number" min={0} placeholder="e.g., 4" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="responseTimeValue"
              label="Response Time Value"
              rules={[
                { required: true, message: 'Please input the response time value!' },
                { type: 'number', transform: Number, min: 0, message: 'Response time must be a non-negative number.' }
              ]}
            >
              <Input type="number" min={0} placeholder="e.g., 1" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="responseTimeUnit"
              label="Response Time Unit"
              rules={[{ required: true, message: 'Please select a response time unit!' }]}
            >
              <Select placeholder="Select unit">
                <Option value="minute">Minute(s)</Option>
                <Option value="hour">Hour(s)</Option>
                <Option value="day">Day(s)</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="isActive"
          label="Status"
          valuePropName="checked"
        >
          <Checkbox>Active</Checkbox>
        </Form.Item>

        {ticketCategory && (
          <div style={{ marginTop: '30px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
            <h3>Assign Priorities</h3>
            <Row gutter={16} align="middle" style={{ marginBottom: '15px' }}>
              <Col span={16}>
                <Select
                  placeholder="Select priority to assign"
                  style={{ width: '100%' }}
                  value={priorityToAssign}
                  onChange={setPriorityToAssign}
                  loading={assigningLoading}
                  disabled={assigningLoading || availablePrioritiesForAssignment.length === 0}
                >
                  {availablePrioritiesForAssignment.map(priority => (
                    <Option key={priority.id} value={priority.id}>
                      {priority.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={8}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAssignPriority}
                  loading={assigningLoading}
                  disabled={!priorityToAssign || assigningLoading}
                >
                  Assign
                </Button>
              </Col>
            </Row>

            <Table
              columns={assignedPrioritiesColumns}
              dataSource={assignedPriorities.sort((a,b) => (a.sortOrder || 999) - (b.sortOrder || 999))}
              rowKey="priorityId"
              pagination={false}
              loading={assigningLoading}
              size="small"
              locale={{ emptyText: 'No priorities assigned yet.' }}
            />
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default TicketCategoryForm;